import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, StatusChip, Loading } from "@/components/common";
import { CheckCircle2, ShieldCheck, AlertTriangle } from "lucide-react";
import { ReasonModal } from "@/components/ReasonModal";
import { toast } from "sonner";

const STATES = ["ready", "in_use", "missing", "damaged", "maintenance_required", "restock_required"];

const STATUS_CHANGE_REASONS = [
  { key: "damaged_field", label: "Damaged in the field" },
  { key: "missing", label: "Missing / not returned" },
  { key: "maintenance", label: "Maintenance required" },
  { key: "restock", label: "Restock required" },
  { key: "restored", label: "Restored to serviceable" },
  { key: "reassigned", label: "Reassigned / relocated" },
  { key: "other", label: "Other" },
];
const REPORT_ISSUE_REASONS = [
  { key: "damaged", label: "Damaged" },
  { key: "missing", label: "Missing" },
  { key: "malfunction", label: "Malfunctioning" },
  { key: "low_supply", label: "Low supply / needs restock" },
  { key: "maintenance_due", label: "Maintenance due" },
  { key: "other", label: "Other" },
];
// Maps a reported problem to the operational status a Manager would confirm on approval.
const ISSUE_TO_STATUS = { damaged: "damaged", missing: "missing", malfunction: "maintenance_required", low_supply: "restock_required", maintenance_due: "maintenance_required" };
const USABILITY_OPTS = [
  { key: "usable", label: "Yes — usable for now", tone: "#10B981" },
  { key: "do_not_use", label: "No — do NOT use", tone: "#EF4444" },
  { key: "unsure", label: "Unsure — review required", tone: "#F59E0B" },
];
const APPROVE_REASONS = [
  { key: "repair_verified", label: "Repair verified" },
  { key: "usable", label: "Equipment usable" },
  { key: "alternate_available", label: "Alternate equipment available" },
  { key: "condition_reviewed", label: "Condition reviewed — safe to proceed" },
  { key: "scope_approved", label: "Scope / pricing approved" },
  { key: "override_approved", label: "Override approved" },
  { key: "other", label: "Other" },
];
const APPROVE_OUTCOMES = [
  { key: "ready", label: "Repaired / Restored / Found — set READY" },
  { key: "damaged", label: "Confirm DAMAGED (stays blocked)" },
  { key: "maintenance_required", label: "Needs Maintenance (stays blocked)" },
  { key: "missing", label: "Confirm MISSING (stays blocked)" },
  { key: "restock_required", label: "Needs Restock" },
];
const DENY_REASONS = [
  { key: "still_damaged", label: "Equipment still damaged" },
  { key: "still_missing", label: "Equipment still missing" },
  { key: "maintenance_incomplete", label: "Maintenance not complete" },
  { key: "safety_concern", label: "Safety concern" },
  { key: "insufficient_info", label: "Insufficient information" },
  { key: "scope_not_approved", label: "Scope / pricing not approved" },
  { key: "must_reschedule", label: "Must reschedule" },
  { key: "other", label: "Other" },
];
const RESTORE_REASONS = [
  { key: "repaired", label: "Repaired" },
  { key: "returned", label: "Returned / found" },
  { key: "restocked", label: "Restocked" },
  { key: "false_alarm", label: "Reported in error" },
  { key: "other", label: "Other" },
];
const OVERRIDE_REASONS = [
  { key: "backup_available", label: "Backup equipment available" },
  { key: "non_critical_task", label: "Task doesn't require this item" },
  { key: "repaired_onsite", label: "Repaired on-site" },
  { key: "customer_urgent", label: "Customer time-sensitive" },
  { key: "other", label: "Other" },
];

export default function Equipment() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [departure, setDeparture] = useState(null); // {ready_to_depart, blocking}
  const [override, setOverride] = useState(null); // shared departure override record
  const [requests, setRequests] = useState([]); // shared status-change requests (pending + history)
  const [modal, setModal] = useState(null); // active ReasonModal config
  const [showAllReq, setShowAllReq] = useState(false);
  const canOverride = user.role === "owner" || user.role === "manager";
  const BLOCKING = ["missing", "damaged", "maintenance_required"];

  const refreshDeparture = useCallback(async () => {
    const [dep, ov] = await Promise.all([
      api.get("/equipment/departure-status", { params: { crew_id: user.crew_id } }),
      api.get("/equipment/departure-override", { params: { crew_id: user.crew_id } }),
    ]);
    setDeparture(dep.data);
    setOverride(ov.data && ov.data.id ? ov.data : null);
  }, [user.crew_id]);

  const refreshRequests = useCallback(async () => {
    const { data } = await api.get("/equipment/status-requests", { params: { status: "all" } });
    setRequests(data);
  }, []);

  const load = async () => {
    const { data } = await api.get("/equipment");
    setItems(data);
    refreshDeparture();
    refreshRequests();
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status, reason) => {
    try {
      const { data } = await api.patch(`/equipment/${id}/status`, { status, reason });
      const restored = !["missing", "damaged", "maintenance_required"].includes(status);
      toast[data.ready_to_depart ? "success" : "warning"](
        restored ? `Restored · ${status.replace(/_/g, " ").toUpperCase()}` : `Status updated · ${status.replace(/_/g, " ").toUpperCase()}`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
      load();
    }
  };
  const askSetStatus = (item, status) => setModal({
    title: `${item.name} → ${status.replace(/_/g, " ").toUpperCase()}`,
    subtitle: "Select a reason for this status change (audited).",
    options: STATUS_CHANGE_REASONS, confirmLabel: "Update Status",
    onConfirm: (reason) => { setModal(null); setStatus(item.id, status, reason); },
  });

  const reportIssue = async (item, payload) => {
    const requested_status = ISSUE_TO_STATUS[payload.issue_type] || item.status;
    try {
      await api.post(`/equipment/${item.id}/status-request`, {
        kind: "report", issue_type: payload.issue_type, usability: payload.secondary,
        requested_status, reason: payload.text,
      });
      toast.success("Issue reported — Manager/Owner notified");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const askReportIssue = (item) => setModal({
    title: `Report Issue · ${item.name}`,
    subtitle: "What is wrong? Then confirm whether it can still be used.",
    options: REPORT_ISSUE_REASONS, confirmLabel: "Report Issue",
    secondaryLabel: "Can this equipment be used?", secondaryOptions: USABILITY_OPTS,
    onConfirm: (text, meta) => { setModal(null); reportIssue(item, { text, issue_type: meta.reason, secondary: meta.secondary }); },
  });

  const toggleCritical = async (item) => {
    try {
      await api.patch(`/equipment/${item.id}/critical`, { critical: !item.critical });
      toast.success(`${item.name} · Critical for Departure ${!item.critical ? "ON" : "OFF"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const decideRequest = async (req, approve, reason, resultStatus) => {
    try {
      const { data } = await api.post(`/equipment/status-requests/${req.id}/approve`, { approve, reason, result_status: resultStatus });
      if (!approve) {
        toast.warning(`Request denied — ${req.equipment_name} stays ${(req.current_status || "").replace(/_/g, " ").toUpperCase()}`);
      } else if (data.restored) {
        toast.success(`${req.equipment_name} restored to ${(data.equipment_status || "ready").replace(/_/g, " ").toUpperCase()}`);
      } else {
        toast.warning(`${req.equipment_name} set to ${(data.equipment_status || "").replace(/_/g, " ").toUpperCase()} (still blocks departure)`);
      }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Decision failed"); }
  };
  const askDecideRequest = (req, approve) => setModal({
    title: `${approve ? "Resolve" : "Deny"} · ${req.equipment_name}`,
    subtitle: approve
      ? "Set the asset's resulting operational status. Choose 'set READY' once it has actually been repaired/restored — this clears the issue everywhere automatically."
      : "Select a denial reason. Denying keeps the current status unchanged.",
    options: approve ? APPROVE_OUTCOMES : DENY_REASONS, requireNoteFor: ["other"],
    confirmLabel: approve ? "Confirm Resolution" : "Deny",
    onConfirm: (reason, meta) => { setModal(null); decideRequest(req, approve, reason, approve ? meta.reason : undefined); },
  });

  const recordOverride = async (reason) => {
    try {
      await api.post("/equipment/checkout", { crew_id: user.crew_id, manager_override: true, override_by: user.name, reason });
      toast.warning("Departure approved by override · blocked equipment still needs repair");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Override failed");
    }
  };
  const askRecordOverride = () => setModal({
    title: "Manager Override & Depart",
    subtitle: "Reason is required and audited.",
    options: OVERRIDE_REASONS, confirmLabel: "Record Override & Depart",
    onConfirm: (reason) => { setModal(null); recordOverride(reason); },
  });

  if (!items) return <Loading label="Loading equipment" />;
  const ds = departure?.display_state;               // ready | override_approved | review_required | not_ready
  const blocking = departure?.blocking || [];
  const review = departure?.review || [];
  const ov = departure?.override || override;        // prefer authoritative payload override
  const pendingByEquip = Object.fromEntries(requests.filter((r) => r.status === "pending").map((r) => [r.equipment_id, r]));

  return (
    <div className="max-w-6xl">
      <SectionTitle sub="Live departure enforcement · critical Missing / Damaged / Maintenance blocks">Equipment</SectionTitle>

      {/* Live departure banner (derived from current state, never manual) */}
      <div className="sg-card p-5 mb-6" data-testid="departure-panel">
        {ds === "ready" ? (
          <div className="p-4 rounded-lg font-head text-xl font-extrabold text-center" data-testid="ready-depart"
            style={{ background: "rgba(16,185,129,0.12)", color: "#10B981" }}>
            <CheckCircle2 className="inline mr-2" size={22} /> READY TO DEPART
          </div>
        ) : ds === "override_approved" ? (
          <div className="p-4 rounded-lg" data-testid="override-approved" style={{ background: "rgba(245,158,11,0.12)", border: "1px solid var(--warn)" }}>
            <div className="font-head text-xl font-extrabold flex items-center gap-2" style={{ color: "#F59E0B" }}>
              <ShieldCheck size={22} /> DEPARTURE APPROVED BY OVERRIDE
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--txt2)" }}>Departure was authorized, but the equipment below is still blocked and must be repaired/restored.</div>
            {ov && (
              <div className="text-sm mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1" style={{ color: "var(--txt2)" }}>
                <div>Approved by: <b style={{ color: "var(--txt)" }}>{ov.approved_by}</b> ({ov.approver_role})</div>
                {ov.created_at && <div>When: {new Date(ov.created_at).toLocaleString()}</div>}
                <div className="sm:col-span-2">Reason: <b style={{ color: "var(--txt)" }}>{ov.reason}</b></div>
              </div>
            )}
            <div className="mt-2 flex flex-col gap-1">
              {blocking.map((b) => (
                <div key={b.id} className="font-mono text-sm" style={{ color: "#FCA5A5" }} data-testid={`blocking-${b.id}`}>
                  {b.name} — {b.status.replace(/_/g, " ").toUpperCase()}
                </div>
              ))}
              {review.map((b) => (
                <div key={b.request_id} className="font-mono text-sm" style={{ color: "#FCD34D" }} data-testid={`review-${b.id}`}>
                  {b.name} — {(b.usability || "").replace(/_/g, " ").toUpperCase()} · “{b.reason}”
                </div>
              ))}
            </div>
          </div>
        ) : ds === "review_required" ? (
          <div className="p-4 rounded-lg" data-testid="review-required" style={{ background: "rgba(245,158,11,0.1)" }}>
            <div className="font-head text-xl font-extrabold flex items-center gap-2" style={{ color: "#F59E0B" }}>
              <AlertTriangle size={22} /> REVIEW REQUIRED
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--txt2)" }}>A critical equipment issue was reported and needs a Manager/Owner decision before departure.</div>
            <div className="mt-2 flex flex-col gap-1">
              {review.map((b) => (
                <div key={b.request_id} className="font-mono text-sm" style={{ color: "#FCD34D" }} data-testid={`review-${b.id}`}>
                  {b.name} — {(b.usability || "").replace(/_/g, " ").toUpperCase()} · “{b.reason}”
                </div>
              ))}
            </div>
            {canOverride && <button className="sg-btn mt-3" onClick={askRecordOverride} data-testid="manager-override"><ShieldCheck size={16} /> Record Manager Override & Depart</button>}
          </div>
        ) : (
          <div className="p-4 rounded-lg" data-testid="not-ready-depart" style={{ background: "rgba(239,68,68,0.1)" }}>
            <div className="font-head text-xl font-extrabold flex items-center gap-2" style={{ color: "#EF4444" }}>
              <AlertTriangle size={22} /> NOT READY TO DEPART
            </div>
            <div className="mt-2 flex flex-col gap-1">
              {blocking.map((b) => (
                <div key={b.id} className="font-mono text-sm" style={{ color: "#FCA5A5" }} data-testid={`blocking-${b.id}`}>
                  {b.name} — {b.status.replace(/_/g, " ").toUpperCase()}
                </div>
              ))}
            </div>
            {canOverride ? (
              <button className="sg-btn mt-3" onClick={askRecordOverride} data-testid="manager-override"><ShieldCheck size={16} /> Record Manager Override & Depart</button>
            ) : (
              <div className="text-sm mt-3" style={{ color: "var(--warn)" }}>A Manager/Owner override (with reason) must be recorded to depart.</div>
            )}
          </div>
        )}
      </div>

      {/* Shared departure override detail — visible to ALL authorized roles */}
      {ov && ds === "override_approved" && (
        <div className="sg-card p-4 mb-6" data-testid="override-banner" style={{ borderColor: "var(--warn)", background: "rgba(245,158,11,0.08)" }}>
          <div className="font-head font-bold flex items-center gap-2" style={{ color: "var(--warn)" }}>
            <ShieldCheck size={18} /> DEPARTURE OVERRIDE ACTIVE
          </div>
          <div className="text-sm mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1" style={{ color: "var(--txt2)" }}>
            <div>Approved by: <b style={{ color: "var(--txt)" }}>{ov.approved_by}</b> ({ov.approver_role})</div>
            {ov.created_at && <div>When: {new Date(ov.created_at).toLocaleString()}</div>}
            <div className="sm:col-span-2">Affected: {ov.affected_equipment?.map((e) => `${e.name} (${e.status})`).join(", ")}</div>
            <div className="sm:col-span-2">Reason: <b style={{ color: "var(--txt)" }}>{ov.reason}</b></div>
          </div>
        </div>
      )}

      {/* Shared Pending Status Requests — operational record across roles */}
      {(() => {
        const pending = requests.filter((r) => r.status === "pending");
        const historyAll = requests.filter((r) => r.status !== "pending");
        const history = showAllReq ? historyAll : historyAll.slice(0, 5);
        if (requests.length === 0) return null;
        return (
          <div className="sg-card p-4 mb-6" data-testid="status-requests-panel">
            <div className="font-head font-bold mb-2">Equipment Status Requests</div>
            {pending.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }} data-testid="no-pending-requests">No pending requests.</div>}
            <div className="flex flex-col gap-2">
              {pending.map((r) => (
                <div key={r.id} className="sg-card-elev p-3" data-testid={`status-request-${r.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm">
                      <b>{r.equipment_name}</b>{" "}
                      {r.kind === "report" ? (
                        <span className="font-mono" style={{ color: r.critical ? "#EF4444" : "var(--warn)" }} data-testid={`request-report-${r.id}`}>
                          REPORTED: {(r.issue_type || "issue").toUpperCase()} · {(r.usability || "").replace(/_/g, " ").toUpperCase() || "N/A"}
                        </span>
                      ) : (
                        <>· <span className="font-mono">{r.current_status}</span> → <span className="font-mono" style={{ color: "var(--accent)" }}>{(r.requested_status || "").toUpperCase()}</span></>
                      )}
                    </div>
                    <StatusChip status="warning" label="Pending" />
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--txt2)" }}>
                    Requested by {r.requested_by_name} ({r.role}) · {new Date(r.created_at).toLocaleString()}
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--txt2)" }}>Reason: <span style={{ color: "var(--txt)" }}>{r.reason}</span></div>
                  {canOverride && (
                    <div className="flex gap-2 mt-2">
                      <button className="sg-btn sg-btn-accent" style={{ minHeight: 38 }} onClick={() => askDecideRequest(r, true)} data-testid={`approve-request-${r.id}`}>Approve</button>
                      <button className="sg-btn" style={{ minHeight: 38 }} onClick={() => askDecideRequest(r, false)} data-testid={`deny-request-${r.id}`}>Deny</button>
                    </div>
                  )}
                  {!canOverride && <div className="text-xs mt-2" style={{ color: "var(--warn)" }}>Awaiting Manager/Owner decision.</div>}
                </div>
              ))}
            </div>
            {historyAll.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Decision History ({historyAll.length})</div>
                  {historyAll.length > 5 && (
                    <button className="text-xs font-semibold" style={{ color: "var(--accent)" }} onClick={() => setShowAllReq((v) => !v)} data-testid="requests-toggle">
                      {showAllReq ? "Collapse" : `View all (${historyAll.length})`}
                    </button>
                  )}
                </div>
                <div className="flex flex-col gap-1" data-testid="status-request-history"
                  style={showAllReq ? { maxHeight: 260, overflowY: "auto" } : undefined}>
                  {history.map((r) => (
                    <div key={r.id} className="text-xs flex flex-wrap gap-1 items-center" data-testid={`history-request-${r.id}`} style={{ color: "var(--txt2)" }}>
                      <StatusChip status={r.status === "approved" ? "ready" : "warning"} label={r.status} />
                      <span><b style={{ color: "var(--txt)" }}>{r.equipment_name}</b> → {((r.resolved_status || r.requested_status) || "").toUpperCase()}</span>
                      <span>· by {r.decided_by} ({r.decided_by_role})</span>
                      {r.decided_at && <span>· {new Date(r.decided_at).toLocaleString()}</span>}
                      {r.decision_reason && <span>· “{r.decision_reason}”</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="equipment-list">
        {items.map((i) => {
          const blockedCritical = i.critical && BLOCKING.includes(i.status);
          const techLocked = user.role === "technician" && blockedCritical;
          return (
          <div key={i.id} className="sg-card p-4 flex items-center justify-between" data-testid={`equip-${i.id}`}>
            <div>
              <div className="font-semibold flex items-center gap-2">
                {i.name}
                {i.critical && (BLOCKING.includes(i.status)
                  ? <span className="chip" data-testid={`critical-badge-${i.id}`} style={{ background: "rgba(239,68,68,0.12)", color: "#EF4444", borderColor: "#EF4444", fontWeight: 700 }}>CRITICAL FOR DEPARTURE</span>
                  : <span className="chip" data-testid={`required-badge-${i.id}`} style={{ background: "var(--bg-elev)", color: "var(--txt2)", borderColor: "var(--brd)" }}>Required for Departure: YES</span>
                )}
              </div>
              {i.specs && <div className="text-xs mt-0.5" style={{ color: "var(--txt2)" }}>{i.specs}</div>}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Current status</span>
                <span data-testid={`current-status-${i.id}`}><StatusChip status={i.status} /></span>
                {pendingByEquip[i.id] && (
                  <span className="chip" data-testid={`issue-status-${i.id}`}
                    style={{ background: pendingByEquip[i.id].critical ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.12)", color: pendingByEquip[i.id].critical ? "#EF4444" : "#F59E0B", borderColor: pendingByEquip[i.id].critical ? "#EF4444" : "#F59E0B", fontWeight: 700 }}>
                    Issue: {pendingByEquip[i.id].critical ? "CRITICAL — IN REVIEW" : "IN REVIEW"}
                  </span>
                )}
              </div>
              {!canOverride && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button className="sg-btn" style={{ minHeight: 40 }} onClick={() => askReportIssue(i)} data-testid={`report-issue-${i.id}`}>Report Equipment Issue</button>
                </div>
              )}
            </div>
            {canOverride ? (
              <div className="flex flex-col items-end gap-2">
                <select className="sg-input" style={{ width: 180 }} value={i.status} onChange={(e) => askSetStatus(i, e.target.value)} data-testid={`equip-status-${i.id}`}>
                  {STATES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <button className="sg-btn" style={{ minHeight: 34, padding: "4px 10px" }} onClick={() => toggleCritical(i)} data-testid={`critical-toggle-${i.id}`}>
                  Critical for Departure: {i.critical ? "YES" : "NO"}
                </button>
              </div>
            ) : (
              <div className="text-xs" style={{ color: "var(--txt2)" }} data-testid={`equip-status-readonly-${i.id}`}>Status is view-only for Technicians</div>
            )}
          </div>
        );})}
      </div>

      {modal && <ReasonModal {...modal} onClose={() => setModal(null)} />}
    </div>
  );
}
